/**
 * audioFormats — the ONE place the app decides which audio files may be
 * uploaded, and which of those a browser can actually play (QA-97).
 *
 * Gordon, verbatim (QA-97): "Audible files should be able to be added to Audio
 * Blocks and Capture."
 *
 * INTERPRETATION, stated plainly because the word "Audible" is ambiguous and the
 * ambiguity is logged as an open question for Gordon (QA-108 item 4). Two
 * readings exist:
 *   (a) Audible-the-service audiobook files (.aa / .aax), and
 *   (b) audiobook/long-form audio formats generally (.m4a / .m4b / .aac / …).
 * This module satisfies BOTH by accepting a superset, because the two readings
 * differ only in how far the accept-list widens and the narrow reading is a
 * strict subset of the wide one. Guessing between them would have been the only
 * way to get it wrong; a superset cannot be wrong in either direction, and the
 * honest playback caveat below is what keeps (a) from over-promising.
 *
 * THE PLAYBACK LIMIT, STATED HONESTLY. Accepting a file is not the same as being
 * able to play it. `.aa` and `.aax` are Audible's DRM-protected containers: no
 * browser can decode them, and neither can a standard `<audio>` element — they
 * can be stored and downloaded, never streamed in-app, and no amount of frontend
 * work changes that (it is a DRM boundary, not a missing codec). `.wma` has no
 * browser decoder either. Rather than render a dead player and let the user
 * conclude the upload failed — the silence-reads-as-broken failure this codebase
 * has paid for repeatedly — the surfaces ASK this module and say so.
 *
 * MIRRORING RULE: `AUDIO_UPLOAD_EXTENSIONS` must stay in step with the backend's
 * `settings.FILE_TYPE_EXTENSIONS["audios"]`. A picker that offers an extension
 * the server rejects is a silent dead-end at the presigned-URL step; a server
 * that allows one the picker hides is a capability nobody can reach.
 */

/**
 * Extensions the audio upload path accepts. Mirrors
 * `settings.FILE_TYPE_EXTENSIONS["audios"]` on the backend.
 *
 * `.mp4` is included for a reason that predates this change: the browser's
 * MediaRecorder captures audio into an mp4/AAC container, so a recorded audio
 * clip legitimately arrives with an `.mp4` extension.
 */
export const AUDIO_UPLOAD_EXTENSIONS = [
  // Already supported before QA-97.
  "mp3",
  "wav",
  "mp4",
  // Mainstream audio containers that were missing — .m4a in particular is what
  // Apple devices and most "export audio" flows produce, and its absence was
  // the most common real dead-end.
  "m4a",
  "aac",
  "oga",
  "ogg",
  "opus",
  "flac",
  "aiff",
  "aif",
  // Audiobook / long-form containers (QA-97).
  "m4b",
  "aa",
  "aax",
  "wma",
];

/**
 * Extensions that no browser can play in an `<audio>` element.
 *
 * `.aa` / `.aax` are Audible DRM containers; `.wma` has no browser decoder.
 * These upload and download fine — they simply cannot stream in-app, and the UI
 * says so rather than showing a player that will never start.
 */
export const AUDIO_NON_PLAYABLE_EXTENSIONS = ["aa", "aax", "wma"];

/** The extension of a filename or URL, lowercased, without the dot. "" if none. */
export const audioExtensionOf = (nameOrUrl) => {
  if (!nameOrUrl) return "";
  // Drop any query string / fragment first: a signed S3 URL ends in "?X-Amz-…",
  // and reading the extension off that yields nonsense.
  const path = String(nameOrUrl).split(/[?#]/)[0];
  const last = path.split("/").pop() || "";
  const dot = last.lastIndexOf(".");
  if (dot < 0 || dot === last.length - 1) return "";
  return last.slice(dot + 1).toLowerCase();
};

/** Is this filename/URL one of the audio types the upload path accepts? */
export const isAcceptedAudioFile = (nameOrUrl) =>
  AUDIO_UPLOAD_EXTENSIONS.includes(audioExtensionOf(nameOrUrl));

/**
 * Can a browser actually PLAY this file?
 *
 * Unknown extensions return `true`: the honest default is to show the player and
 * let the browser answer, rather than pre-emptively telling a user their file is
 * unplayable on a guess. Only the formats we KNOW cannot decode are called out.
 */
export const isBrowserPlayableAudio = (nameOrUrl) => {
  const ext = audioExtensionOf(nameOrUrl);
  if (!ext) return true;
  return !AUDIO_NON_PLAYABLE_EXTENSIONS.includes(ext);
};

/**
 * What to tell the user about a stored-but-unplayable audio file. Null when the
 * file plays normally, so a caller renders nothing in the common case.
 */
export const audioPlaybackNotice = (nameOrUrl) => {
  const ext = audioExtensionOf(nameOrUrl);
  if (isBrowserPlayableAudio(nameOrUrl)) return null;
  if (ext === "aa" || ext === "aax") {
    return "Saved. Audible files (.aa/.aax) are DRM-protected, so they can't play in the browser — download it to listen in the Audible app.";
  }
  return `Saved. .${ext} files can't play in the browser — download it to listen.`;
};

/** The `accept` attribute for an audio file input, built from the one list. */
export const audioAcceptAttribute = () =>
  AUDIO_UPLOAD_EXTENSIONS.map((ext) => `.${ext}`).join(",");
